import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, IconButton, Chip } from '@mui/material';
import { Settings, Home, Numbers, Tune, Warehouse, AccountBalance, Receipt, Grade, CurrencyExchange, People, Group, Security, Business, ImportExport, AccountTree, School, Email } from '@mui/icons-material';

const setupCards = [
  { label: 'Company Details',        description: 'School name, logo, address, bank & registration for documents',  icon: Business,          color: '#0a1931', path: '/setups/company',                 enabled: true },
  { label: 'Email / SMTP Setup',     description: "Send mail from your school's own mailbox (BC Email Account)",     icon: Email,             color: '#00838f', path: '/setups/mail',                    enabled: true },
  { label: 'Number Series',          description: 'Configure automatic numbering for documents',                     icon: Numbers,          color: '#1976d2', path: '/setups/number-series',           enabled: true },
  { label: 'Locations',              description: 'Manage stores, warehouses and locations',                         icon: Warehouse,         color: '#2e7d32', path: '/setups/locations',               enabled: true },
  { label: 'Posting Setup',          description: 'Map transaction types to GL accounts (AR, advance receipts)',     icon: Tune,              color: '#6a1b9a', path: '/setups/posting',                 enabled: true },
  { label: 'Learner Posting Groups', description: 'Map learner categories to AR and advance GL accounts (BC-style)', icon: AccountBalance,    color: '#0288d1', path: '/setups/learner-posting-groups',  enabled: true },
  { label: 'VAT Posting Groups',     description: 'Define VAT rates and output/input VAT GL accounts per charge',   icon: Receipt,           color: '#f57c00', path: '/setups/vat-posting-groups',      enabled: true },
  { label: 'Grading Scale (CBC)',    description: 'Configure EE/ME/AE/BE percentage boundaries and colors',          icon: Grade,             color: '#1565c0', path: '/setups/grading-scale',           enabled: true },
  { label: 'Currencies',             description: 'Manage ISO currencies and set the local (functional) currency',   icon: CurrencyExchange,  color: '#00695c', path: '/setups/currencies',              enabled: true },
  { label: 'Users',                  description: 'Create and manage system user accounts',                           icon: People,            color: '#4527a0', path: '/setups/users',                   enabled: true },
  { label: 'User Groups',            description: 'Group users and assign shared permission sets',                   icon: Group,             color: '#00695c', path: '/setups/user-groups',             enabled: true },
  { label: 'Permission Sets',        description: 'Define module-level access rights (View, Create, Edit, Delete, Post)', icon: Security,      color: '#c62828', path: '/setups/permission-sets',         enabled: true },
  { label: 'Data Import / Export',   description: 'Export a collection to Excel, edit it, and re-import (create or update — never deletes)', icon: ImportExport, color: '#37474f', path: '/setups/data-import', enabled: true },
  { label: 'Approval Workflows',     description: 'Require approval on documents (Purchase Orders, Leave) — one or more approvers, sequential or parallel', icon: AccountTree, color: '#5d4037', path: '/setups/approvals', enabled: true },
];

const SetupsList = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Add school inquiries card only for super admins
  const allCards = isSuperAdmin
    ? [
        { label: 'School Inquiries',      description: 'Review and approve new school setup requests',                    icon: School,           color: '#1565c0', path: '/admin/school-inquiries', enabled: true, badge: 'Admin' },
        ...setupCards,
      ]
    : setupCards;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Settings sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">
                Setups
              </Typography>
            </Box>
            <IconButton
              color="primary"
              onClick={() => navigate('/dashboard')}
              title="Back to Dashboard"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}
            >
              <Home />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            {allCards.map(({ label, description, icon: Icon, color, path, enabled, badge }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Card
                  sx={{
                    borderRadius: 3,
                    cursor: enabled ? 'pointer' : 'default',
                    opacity: enabled ? 1 : 0.6,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': enabled ? { boxShadow: 6, transform: 'translateY(-4px)' } : {}
                  }}
                  onClick={() => enabled && navigate(path)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          flexShrink: 0,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${color}1A`
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color }} />
                      </Box>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="h6" fontWeight={700}>
                            {label}
                          </Typography>
                          {badge && <Chip label={badge} size="small" color="primary" variant="outlined" />}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SetupsList;
